import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PlatformService } from './platform.service';

const EXPANDED_CLASS = 'uvSheetExpanded';
// Host class of the dialogs that stay centered on a phone (confirmations).
const ALERT_DIALOG_CLASS = 'uvDialogAlert';
const TAP_TOLERANCE = 6;
const MIN_DRAG_HEIGHT = 80;
// Released below this share of its natural height, a sheet closes.
const CLOSE_RATIO = 0.6;

export interface SheetGrabTarget {
  // The element that grows or shrinks, anchored to the bottom edge.
  panel: HTMLElement;
  close: () => void;
  canClose: () => boolean;
}

// Drag handle on top of every sheet on a phone. Dragged up the sheet grows to
// the full screen, dragged down it closes; a tap switches between the two
// heights. Dialogs get it here; bottom sheets carry urbanSheetGrab.
@Injectable({
  providedIn: 'root'
})
export class SheetGrabService {
  private readonly zone = inject(NgZone);
  private readonly isMobile = inject(PlatformService).isMobileViewport;

  constructor() {
    inject(MatDialog).afterOpened.subscribe((ref: MatDialogRef<unknown>) => {
      if (this.isMobile()) {
        this.attachToDialog(ref);
      }
    });
  }

  bind(grab: HTMLElement, target: SheetGrabTarget): void {
    this.zone.runOutsideAngular(() => this.listen(grab, target));
  }

  // The container's id is a host binding, not there yet: start from the content.
  private attachToDialog(ref: MatDialogRef<unknown>): void {
    const content: HTMLElement | undefined = ref.componentRef?.location.nativeElement;
    if (content?.classList.contains(ALERT_DIALOG_CLASS)) {
      return;
    }

    const surface: HTMLElement | null = content?.closest<HTMLElement>('.mat-mdc-dialog-surface') ?? null;
    const pane: HTMLElement | null = content?.closest<HTMLElement>('.cdk-overlay-pane') ?? null;

    if (!surface || !pane) {
      return;
    }

    const grab: HTMLElement = document.createElement('div');
    grab.className = 'uvSheetGrab';
    grab.setAttribute('aria-hidden', 'true');
    surface.prepend(grab);

    this.bind(grab, {
      panel: pane,
      close: () => ref.close(),
      canClose: () => !ref.disableClose
    });
  }

  private listen(grab: HTMLElement, { panel, close, canClose }: SheetGrabTarget): void {
    let pointerId: number | null = null;
    let startY: number = 0;
    let startHeight: number = 0;
    let naturalHeight: number = 0;
    let maxHeight: number = 0;
    let wasExpanded: boolean = false;
    let savedStyle: string = '';

    grab.addEventListener('pointerdown', (event: PointerEvent) => {
      if (pointerId !== null) {
        return;
      }

      const rect: DOMRect = panel.getBoundingClientRect();
      pointerId = event.pointerId;
      startY = event.clientY;
      startHeight = rect.height;
      // The body is padded by the safe area: the sheet stops under the status bar.
      maxHeight = rect.bottom - parseFloat(getComputedStyle(document.body).paddingTop);
      savedStyle = panel.style.cssText;
      wasExpanded = panel.classList.contains(EXPANDED_CLASS);

      panel.classList.remove(EXPANDED_CLASS);
      naturalHeight = panel.getBoundingClientRect().height;
      this.setHeight(panel, startHeight);

      grab.setPointerCapture(event.pointerId);
      event.preventDefault();
    });

    grab.addEventListener('pointermove', (event: PointerEvent) => {
      if (event.pointerId === pointerId) {
        const height: number = startHeight + startY - event.clientY;
        this.setHeight(panel, Math.min(Math.max(height, MIN_DRAG_HEIGHT), maxHeight));
      }
    });

    grab.addEventListener('pointerup', (event: PointerEvent) => {
      if (event.pointerId !== pointerId) {
        return;
      }

      pointerId = null;
      const moved: number = startY - event.clientY;
      const height: number = panel.getBoundingClientRect().height;
      panel.style.cssText = savedStyle;

      if (Math.abs(moved) < TAP_TOLERANCE) {
        panel.classList.toggle(EXPANDED_CLASS, !wasExpanded);
      } else if (height < naturalHeight * CLOSE_RATIO && canClose()) {
        this.zone.run(close);
      } else {
        panel.classList.toggle(EXPANDED_CLASS, height > (naturalHeight + maxHeight) / 2);
      }
    });

    grab.addEventListener('pointercancel', (event: PointerEvent) => {
      if (event.pointerId === pointerId) {
        pointerId = null;
        panel.style.cssText = savedStyle;
        panel.classList.toggle(EXPANDED_CLASS, wasExpanded);
      }
    });
  }

  private setHeight(panel: HTMLElement, height: number): void {
    panel.style.setProperty('height', `${height}px`, 'important');
    panel.style.setProperty('max-height', 'none', 'important');
  }
}
